import React, { useEffect, useRef, useState } from "react";
import {
  createKeystrokeLog,
  logKeystroke,
  buildSubmitPayload,
} from "../../utils/typingTestEngine";

const BLOCKED_NAV_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"];

// Candidate-runtime player for the Typing Test question type.
// Mounted from candidateTest.js the same way Main (coding) is mounted for question types 7/12.
// The passage is the question's own `question` field — same as every other question type —
// fetched by the existing getQuestion/getQuestionDetail flow in candidateTest.js, so no
// separate passage fetch is needed here.
// Live WPM/accuracy/errors/time-left are intentionally not shown here — those only surface
// afterwards in the report — so this only tracks progress and lets the candidate submit.
export default function TypingTestPlayer({ question, onSubmit, onProgress }) {
  const passageText = question?.question || "";

  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const startedAtRef = useRef(null);
  const keystrokeLogRef = useRef(createKeystrokeLog());
  const backspaceCountRef = useRef(0);
  // Running total of mistyped characters — only ever grows, so correcting a mistake with
  // backspace does not make it disappear from the error count.
  const cumulativeErrorsRef = useRef(0);
  // Only set once a submission has actually been confirmed successful — NOT the instant Submit
  // is clicked — so a failed submitTypingResult call (e.g. backend not deployed yet, a network
  // blip) doesn't permanently lock the candidate out of retrying.
  const submittedRef = useRef(false);
  const inputRef = useRef(null);
  const currentCharRef = useRef(null);

  useEffect(() => {
    currentCharRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [typed.length]);

  const submitResult = async (finalTyped, now) => {
    if (submittedRef.current || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    const payload = buildSubmitPayload({
      passage: passageText,
      typed: finalTyped,
      keystrokeLog: keystrokeLogRef.current,
      startedAt: startedAtRef.current || now,
      submittedAt: now,
      backspaceCount: backspaceCountRef.current,
      cumulativeErrorCount: cumulativeErrorsRef.current,
    });
    const ok = await onSubmit?.(payload);
    if (ok === false) {
      // Submission failed — leave the candidate's typed text and progress intact so Submit
      // (or finishing the passage again) can simply be retried.
      setSubmitting(false);
      setSubmitError("Submission failed. Please try again.");
      return;
    }
    submittedRef.current = true;
    setSubmitting(false);
  };

  const handleKeyDown = (e) => {
    if (BLOCKED_NAV_KEYS.includes(e.key)) {
      e.preventDefault();
      return;
    }
    if (e.key === "Backspace") {
      backspaceCountRef.current += 1;
    }
  };

  const handleChange = (e) => {
    if (submittedRef.current || submitting) return;
    const value = e.target.value;
    // Strictly forward-only: never allow typing past the end of the passage.
    if (value.length > passageText.length) return;

    const now = Date.now();
    if (!startedAtRef.current && value.length > 0) {
      startedAtRef.current = now;
    }

    if (value.length > typed.length) {
      const newChars = value.slice(typed.length);
      for (let i = 0; i < newChars.length; i++) {
        const ch = newChars[i];
        logKeystroke(keystrokeLogRef.current, ch, now);
        // Count the mistake at the moment it's typed — not derived later from `typed` vs
        // `passage` — so a subsequent backspace correction can't erase it from the total.
        if (ch !== passageText[typed.length + i]) {
          cumulativeErrorsRef.current += 1;
        }
      }
    }

    setTyped(value);

    onProgress?.(buildSubmitPayload({
      passage: passageText,
      typed: value,
      keystrokeLog: keystrokeLogRef.current,
      startedAt: startedAtRef.current || now,
      submittedAt: now,
      backspaceCount: backspaceCountRef.current,
      cumulativeErrorCount: cumulativeErrorsRef.current,
    }));

    if (value.length === passageText.length && passageText.length > 0) {
      submitResult(value, now);
    }
  };

  const blockClipboard = (e) => e.preventDefault();
  const focusInput = () => inputRef.current?.focus();

  const progressPct = passageText.length > 0 ? Math.min(100, (typed.length / passageText.length) * 100) : 0;

  return (
    <div
      className="typing-test-player"
      style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", margin: "auto", width: "90vw", }}
    >
      <div className="typing-progress-bar" style={{ height: 6, background: "#E4E9F0", borderRadius: 4, flexShrink: 0 }}>
        <div
          style={{
            width: `${progressPct}%`,
            height: "100%",
            background: "#F75891",
            borderRadius: 4,
            transition: "width 0.15s linear",
          }}
        />
      </div>

      {/* position: relative so the tooltip below can anchor to this wrapper rather than to the
          scrollable passage panel — anchoring inside the panel would get clipped by its own
          overflow-y: auto the moment the tooltip pokes out above the first line. */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        {typed.length === 0 && (
          <div
            className="typing-start-tooltip"
            style={{
              position: "absolute",
              top: -42,
              left: 24,
              background: "#2B2B2B",
              color: "#FFFFFF",
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            Start Typing!
            <span
              style={{
                position: "absolute",
                top: "100%",
                left: 20,
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid #2B2B2B",
              }}
            />
          </div>
        )}

        {/* The actual input capture (below) is visually hidden — this passage panel is the
            entire visual surface, so the whole panel is clickable to (re)focus typing. */}
        <div
          className="typing-passage-panel"
          style={{
            maxHeight: 220,
            overflowY: "auto",
            padding: 24,
            border: "1px solid #E4E9F0",
            borderRadius: 8,
            fontSize: 22,
            lineHeight: 1.9,
            letterSpacing: 0.5,
            userSelect: "none",
            cursor: "text",
          }}
          onClick={focusInput}
          onCopy={blockClipboard}
          onCut={blockClipboard}
          onContextMenu={blockClipboard}
        >
          {passageText.split("").map((ch, i) => {
            let className = "typing-char-pending";
            let color = "#8A9199";
            if (i < typed.length) {
              const isCorrect = typed[i] === ch;
              className = isCorrect ? "typing-char-correct" : "typing-char-incorrect";
              color = isCorrect ? "#2E7D32" : "#D32F2F";
            } else if (i === typed.length) {
              className = "typing-char-current";
            }
            const isCurrent = i === typed.length;
            return (
              <span
                key={i}
                ref={isCurrent ? currentCharRef : null}
                className={className}
                style={{
                  color,
                  background: isCurrent ? "#FCE4EC" : "transparent",
                }}
              >
                {ch}
              </span>
            );
          })}
        </div>
      </div>

      {/* Visually hidden (not display:none, so it stays focusable and keeps receiving
          keystrokes) — this textarea is the real input, the passage panel above is what
          the candidate actually looks at and types against. */}
      <textarea
        ref={inputRef}
        className="typing-input-capture form-control"
        value={typed}
        disabled={!passageText || submitting || submittedRef.current}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onPaste={blockClipboard}
        onCopy={blockClipboard}
        onCut={blockClipboard}
        autoFocus
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />

      <div style={{ marginTop: 16, textAlign: "right", flexShrink: 0 }}>
        {submitError && (
          <div style={{ color: "#D32F2F", fontSize: 13, marginBottom: 8 }}>{submitError}</div>
        )}
        <button
          type="button"
          disabled={!typed || submitting || submittedRef.current}
          onClick={() => submitResult(typed, Date.now())}
          style={{
            background: !typed || submitting || submittedRef.current ? "#8A9199" : "#F75891",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 6,
            padding: "10px 28px",
            fontWeight: 600,
            fontSize: 14,
            cursor: !typed || submitting || submittedRef.current ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
}
